import { ApiResponseError } from '@app/utils/api';
import type {
  OutputExport as ApiOutputExport,
  OutputExportFormat,
  OutputExportMember as ApiOutputExportMember,
  OutputExportRequest as ApiOutputExportRequest,
  OutputExportBundleMode,
  OutputExportPurpose,
  OutputExportStatus,
} from '@reap/openapi-public-v1';

import {
  parseUploadedFileRecord,
  type ChatArtifactLike,
  type UploadedFileRecord,
} from './chatArtifactUtils';

export type {
  OutputExportBundleMode,
  OutputExportFormat,
  OutputExportPurpose,
  OutputExportStatus,
} from '@reap/openapi-public-v1';

export type OutputExportMemberRecord = Pick<ApiOutputExportMember, 'resourceRef' | 'fileName'> &
  Partial<Pick<ApiOutputExportMember, 'contentType' | 'sizeBytes' | 'format'>>;

export type OutputExportRecord = Pick<ApiOutputExport, 'status' | 'purpose' | 'bundleMode'> & {
  id: string;
  fileName?: string | null;
  contentType?: string | null;
  sizeBytes?: number;
  sourceResourceRefs: string[];
  members: OutputExportMemberRecord[];
  downloadPath?: string | null;
  expiresAt?: string | null;
  promotedFileRef?: string | null;
};

export type OutputExportRequestRecord = Omit<ApiOutputExportRequest, 'resourceRefs'> & {
  resourceRefs: string[];
};

export interface PromotedFileUpdateRecord {
  name?: string;
  filename?: string;
  description?: string;
}

export interface ManagedFileWithEtag {
  file: UploadedFileRecord;
  etag: string | null;
}

export interface DownloadedOutputExport {
  exportRecord: OutputExportRecord;
  blob: Blob;
  fileName: string;
  contentType: string;
}

export type OrgApiRequester = (resource: string, init?: RequestInit) => Promise<Response>;

const OUTPUT_EXPORT_FILE_EXTENSION: Record<OutputExportFormat, string> = {
  txt: '.txt',
  json: '.json',
  csv: '.csv',
  xlsx: '.xlsx',
  pdf: '.pdf',
  png: '.png',
  zip: '.zip',
};

const DEFAULT_OUTPUT_EXPORT_READY_ATTEMPTS = 24;
const DEFAULT_OUTPUT_EXPORT_READY_INTERVAL_MS = 500;

const resolveTimestampValue = (value: Date | string | null | undefined): number => {
  if (value instanceof Date) {
    return value.getTime();
  }
  if (typeof value === 'string' && value.trim().length > 0) {
    return Date.parse(value) || 0;
  }
  return 0;
};

export const OUTPUT_EXPORT_FORMAT_LABELS: Record<OutputExportFormat, string> = {
  txt: 'TXT',
  json: 'JSON',
  csv: 'CSV',
  xlsx: 'XLSX',
  pdf: 'PDF',
  png: 'PNG',
  zip: 'ZIP',
};

const ensureOk = async (response: Response, fallback: string): Promise<Response> => {
  if (!response.ok) {
    throw new ApiResponseError(response, await buildApiErrorMessage(response, fallback));
  }
  return response;
};

const buildApiErrorMessage = async (response: Response, fallback: string): Promise<string> => {
  let detail = '';
  try {
    const cloned = response.clone();
    const contentType = cloned.headers.get('Content-Type')?.toLowerCase() ?? '';
    if (contentType.includes('json')) {
      const body = (await cloned.json()) as { detail?: unknown; title?: unknown };
      if (typeof body.detail === 'string' && body.detail.trim()) {
        detail = body.detail.trim();
      } else if (typeof body.title === 'string' && body.title.trim()) {
        detail = body.title.trim();
      }
    } else {
      const text = (await cloned.text()).trim();
      if (text) {
        detail = text;
      }
    }
  } catch {
    detail = '';
  }
  if (!detail) {
    return `${fallback} (status ${response.status}).`;
  }
  return `${fallback}: ${detail} (status ${response.status}).`;
};

const sanitizeFileStem = (value?: string | null): string => {
  const trimmed = (value ?? '').trim();
  if (!trimmed) {
    return 'download';
  }
  const stem = trimmed.replace(/[^a-zA-Z0-9._-]+/g, '_').replace(/^_+|_+$/g, '');
  return stem || 'download';
};

const sanitizeDownloadFileName = (value?: string | null): string | null => {
  const trimmed = (value ?? '').trim();
  if (!trimmed) {
    return null;
  }
  const leafName = trimmed.split(/[/\\]+/).pop() ?? trimmed;
  const withoutControls = leafName.replace(/[\u0000-\u001f\u007f]+/g, '').trim();
  if (!withoutControls) {
    return null;
  }
  const sanitized = withoutControls
    .replace(/[^a-zA-Z0-9._-]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .replace(/^\.+/, '');
  return sanitized || null;
};

const isOutputExportFormat = (value: unknown): value is OutputExportFormat =>
  typeof value === 'string' &&
  ['txt', 'json', 'csv', 'xlsx', 'pdf', 'png', 'zip'].includes(value.trim().toLowerCase());

const parseOutputExportMemberRecord = (value: Record<string, unknown>): OutputExportMemberRecord | null => {
  const resourceRef = typeof value.resourceRef === 'string' ? value.resourceRef : undefined;
  const fileName = typeof value.fileName === 'string' ? value.fileName : undefined;
  if (!resourceRef || !fileName) {
    return null;
  }
  return {
    resourceRef,
    fileName,
    contentType: typeof value.contentType === 'string' ? value.contentType : undefined,
    sizeBytes: typeof value.sizeBytes === 'number' ? value.sizeBytes : undefined,
    format: isOutputExportFormat(value.format) ? value.format : undefined,
  };
};

const parseOutputExportStatus = (value: unknown): OutputExportStatus | null => {
  const normalized = typeof value === 'string' ? value.trim().toLowerCase() : '';
  switch (normalized) {
    case 'queued':
    case 'running':
    case 'ready':
    case 'failed':
    case 'expired':
      return normalized as OutputExportStatus;
    default:
      return null;
  }
};

const parseOutputExportPurpose = (value: unknown): OutputExportPurpose | null => {
  switch (typeof value === 'string' ? value.trim().toLowerCase() : '') {
    case 'ui_download':
    case 'ticket_delivery_explicit':
    case 'ticket_delivery_automatic':
    case 'promotion':
      return (typeof value === 'string' ? value.trim().toLowerCase() : '') as OutputExportPurpose;
    default:
      return null;
  }
};

const parseOutputExportBundleMode = (value: unknown): OutputExportBundleMode | null => {
  switch (typeof value === 'string' ? value.trim().toLowerCase() : '') {
    case 'single':
    case 'zip':
    case 'separate':
      return (typeof value === 'string' ? value.trim().toLowerCase() : '') as OutputExportBundleMode;
    default:
      return null;
  }
};

export const parseOutputExportRecord = (value: unknown): OutputExportRecord | null => {
  if (!value || typeof value !== 'object') {
    return null;
  }
  const record = value as Record<string, unknown>;
  const id = typeof record.id === 'string' ? record.id.trim() : '';
  const status = parseOutputExportStatus(record.status);
  const purpose = parseOutputExportPurpose(record.purpose);
  const bundleMode = parseOutputExportBundleMode(record.bundleMode);
  const sourceResourceRefs = Array.isArray(record.sourceResourceRefs)
    ? record.sourceResourceRefs.filter((entry): entry is string => typeof entry === 'string' && entry.trim().length > 0)
    : [];
  const members: OutputExportMemberRecord[] = Array.isArray(record.members)
    ? record.members
        .filter((entry): entry is Record<string, unknown> => Boolean(entry) && typeof entry === 'object')
        .reduce<OutputExportMemberRecord[]>((acc, entry) => {
          const parsed = parseOutputExportMemberRecord(entry);
          if (parsed) {
            acc.push(parsed);
          }
          return acc;
        }, [])
    : [];
  if (!id || !status || !purpose || !bundleMode || sourceResourceRefs.length === 0 || members.length === 0) {
    return null;
  }
  return {
    id,
    status,
    purpose,
    bundleMode,
    fileName: typeof record.fileName === 'string' ? record.fileName : undefined,
    contentType: typeof record.contentType === 'string' ? record.contentType : undefined,
    sizeBytes: typeof record.sizeBytes === 'number' ? record.sizeBytes : undefined,
    sourceResourceRefs,
    members,
    downloadPath: typeof record.downloadPath === 'string' ? record.downloadPath : undefined,
    expiresAt: typeof record.expiresAt === 'string' ? record.expiresAt : undefined,
    promotedFileRef: typeof record.promotedFileRef === 'string' ? record.promotedFileRef : undefined,
  };
};

export const parseFilesListResponse = (value: unknown): UploadedFileRecord[] => {
  if (!value || typeof value !== 'object') {
    return [];
  }
  const items = Array.isArray((value as Record<string, unknown>).items)
    ? ((value as Record<string, unknown>).items as unknown[])
    : [];
  return items
    .map((entry) => parseUploadedFileRecord(entry))
    .filter((entry): entry is UploadedFileRecord => Boolean(entry));
};

const sleep = async (ms: number): Promise<void> =>
  new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });

const buildOutputExportDownloadPath = (exportRecord: OutputExportRecord): string =>
  (exportRecord.downloadPath ?? `chat/exports/${encodeURIComponent(exportRecord.id)}/download`).replace(/^\//, '');

export const parseContentDispositionFileName = (value?: string | null): string | null => {
  const header = (value ?? '').trim();
  if (!header) {
    return null;
  }
  const utf8Match = header.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) {
    try {
      return sanitizeDownloadFileName(decodeURIComponent(utf8Match[1]));
    } catch {
      return sanitizeDownloadFileName(utf8Match[1]);
    }
  }
  const quotedMatch = header.match(/filename=\"([^\"]+)\"/i);
  if (quotedMatch?.[1]) {
    return sanitizeDownloadFileName(quotedMatch[1]);
  }
  const plainMatch = header.match(/filename=([^;]+)/i);
  return sanitizeDownloadFileName(plainMatch?.[1]?.trim());
};

export const buildFallbackExportFileName = (
  fallbackStem: string,
  format?: OutputExportFormat,
): string => `${sanitizeFileStem(fallbackStem)}${format ? OUTPUT_EXPORT_FILE_EXTENSION[format] : ''}`;

export const getOutputExport = async (
  requestOrgApi: OrgApiRequester,
  exportId: string,
): Promise<OutputExportRecord> => {
  const response = await ensureOk(
    await requestOrgApi(`chat/exports/${encodeURIComponent(exportId)}`, {
      headers: { Accept: 'application/json' },
    }),
    'Failed to load output export',
  );
  const parsed = parseOutputExportRecord(await response.json());
  if (!parsed) {
    throw new Error('Export response did not match OutputExport.');
  }
  return parsed;
};

export const waitForOutputExportReady = async (
  requestOrgApi: OrgApiRequester,
  exportRecord: OutputExportRecord,
  attempts = DEFAULT_OUTPUT_EXPORT_READY_ATTEMPTS,
  intervalMs = DEFAULT_OUTPUT_EXPORT_READY_INTERVAL_MS,
): Promise<OutputExportRecord> => {
  let current = exportRecord;
  for (let attempt = 0; attempt < attempts; attempt += 1) {
    if (current.status === 'ready') {
      return current;
    }
    if (current.status === 'failed') {
      throw new Error('The requested export failed to materialize.');
    }
    if (current.status === 'expired') {
      throw new Error('The requested export expired before it could be downloaded.');
    }
    await sleep(intervalMs);
    current = await getOutputExport(requestOrgApi, current.id);
  }
  if (current.status !== 'ready') {
    throw new Error('The requested export is still not ready. Please retry.');
  }
  return current;
};

export const createOutputExport = async (
  requestOrgApi: OrgApiRequester,
  request: OutputExportRequestRecord,
): Promise<OutputExportRecord> => {
  const response = await ensureOk(
    await requestOrgApi('chat/exports', {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    }),
    'Failed to create output export',
  );
  const parsed = parseOutputExportRecord(await response.json());
  if (!parsed) {
    throw new Error('Export response did not match OutputExport.');
  }
  return parsed;
};

export const downloadOutputExportBytes = async (
  requestOrgApi: OrgApiRequester,
  exportRecord: OutputExportRecord,
  fallbackStem: string,
  fallbackFormat?: OutputExportFormat,
): Promise<DownloadedOutputExport> => {
  const readyExport = exportRecord.status === 'ready' ? exportRecord : await waitForOutputExportReady(requestOrgApi, exportRecord);
  const response = await ensureOk(
    await requestOrgApi(buildOutputExportDownloadPath(readyExport), {
      headers: {
        Accept: readyExport.contentType ?? 'application/octet-stream',
      },
    }),
    'Failed to download output export',
  );
  const blob = await response.blob();
  const headerName = parseContentDispositionFileName(response.headers.get('Content-Disposition'));
  const fileName = headerName ?? readyExport.fileName ?? buildFallbackExportFileName(fallbackStem, fallbackFormat);
  return {
    exportRecord: readyExport,
    blob,
    fileName,
    contentType: response.headers.get('Content-Type') ?? readyExport.contentType ?? 'application/octet-stream',
  };
};

export const createAndDownloadOutputExport = async (
  requestOrgApi: OrgApiRequester,
  request: OutputExportRequestRecord,
  fallbackStem: string,
): Promise<DownloadedOutputExport> => {
  const exportRecord = await createOutputExport(requestOrgApi, request);
  return downloadOutputExportBytes(requestOrgApi, exportRecord, fallbackStem, request.format);
};

export const listPromotedFiles = async (
  requestOrgApi: OrgApiRequester,
  query = '',
  limit = 50,
): Promise<UploadedFileRecord[]> => {
  const params = new URLSearchParams();
  params.set('sourceKind', 'artifact_promotion');
  params.set('limit', String(limit));
  if (query.trim()) {
    params.set('q', query.trim());
  }
  const response = await ensureOk(
    await requestOrgApi(`files?${params.toString()}`, {
      headers: { Accept: 'application/json' },
    }),
    'Failed to list promoted files',
  );
  const items = parseFilesListResponse(await response.json());
  return items.sort((left, right) => {
    const leftTime = resolveTimestampValue(left.updatedAt ?? left.createdAt);
    const rightTime = resolveTimestampValue(right.updatedAt ?? right.createdAt);
    return rightTime - leftTime;
  });
};

export const getManagedFileWithEtag = async (
  requestOrgApi: OrgApiRequester,
  fileId: string,
): Promise<ManagedFileWithEtag> => {
  const response = await ensureOk(
    await requestOrgApi(`files/${encodeURIComponent(fileId)}`, {
      headers: { Accept: 'application/json' },
    }),
    'Failed to load managed file',
  );
  const file = parseUploadedFileRecord(await response.json());
  if (!file) {
    throw new Error('File response did not match Fileschema.');
  }
  return {
    file,
    etag: response.headers.get('ETag'),
  };
};

export const updatePromotedFile = async (
  requestOrgApi: OrgApiRequester,
  fileId: string,
  ifMatch: string,
  update: PromotedFileUpdateRecord,
): Promise<ManagedFileWithEtag> => {
  const response = await ensureOk(
    await requestOrgApi(`files/${encodeURIComponent(fileId)}`, {
      method: 'PATCH',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'If-Match': ifMatch,
      },
      body: JSON.stringify(update),
    }),
    'Failed to update promoted file',
  );
  const file = parseUploadedFileRecord(await response.json());
  if (!file) {
    throw new Error('Updated file response did not match Fileschema.');
  }
  return {
    file,
    etag: response.headers.get('ETag'),
  };
};

export const deletePromotedFile = async (
  requestOrgApi: OrgApiRequester,
  fileId: string,
  ifMatch: string,
): Promise<void> => {
  await ensureOk(
    await requestOrgApi(`files/${encodeURIComponent(fileId)}`, {
      method: 'DELETE',
      headers: { 'If-Match': ifMatch },
    }),
    'Failed to delete promoted file',
  );
};

export const resolveArtifactExportFormats = (artifact: ChatArtifactLike): OutputExportFormat[] => {
  const kind = String(artifact.kind ?? '').trim().toLowerCase();
  const mime = String(artifact.mimeType ?? '').trim().toLowerCase();
  if (kind === 'table') {
    return ['csv', 'xlsx'];
  }
  if (kind === 'chart' || kind === 'topology' || mime.startsWith('image/')) {
    return ['png'];
  }
  if (kind === 'report' || kind === 'document' || mime === 'application/pdf') {
    return ['pdf'];
  }
  if (mime.includes('json') || kind === 'object') {
    return ['json'];
  }
  if (kind === 'commandoutput' || kind === 'config' || mime.startsWith('text/')) {
    return ['txt'];
  }
  return [];
};

export const resolveDefaultArtifactExportFormat = (artifact: ChatArtifactLike): OutputExportFormat | undefined =>
  resolveArtifactExportFormats(artifact)[0];
